// src/components/ChatApp.js
import React, { useState, useEffect, useLayoutEffect } from 'react';
import './ChatApp.css';
import Sidebar from './Sidebar';
import ChatWindow from './ChatWindow';
import AppMenu from './AppMenu';
import TemplatesPage from './TemplatesPage';
import { api } from '../api/robyBackend';

const SIDEBAR_WIDTH_PX = 300;
const DESKTOP_BREAKPOINT = 1024;

const ChatApp = ({ token, setToken }) => {
  const [sessions, setSessions] = useState([]);
  const [app, setApp] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null); // Track session by ID
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showTemplatesPage, setShowTemplatesPage] = useState(false);

  // (1) Sidebar-Breite als grobe Basis (kann bleiben)
  useLayoutEffect(() => {
    const apply = () => {
      const desktop = window.innerWidth >= DESKTOP_BREAKPOINT;
      const w = isSidebarVisible && desktop ? SIDEBAR_WIDTH_PX : 0;
      document.documentElement.style.setProperty('--sidebar-w', `${w}px`);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [isSidebarVisible]);

  // (2) Chat-Spaltenkanten exakt messen => --chat-left / --chat-right
  useLayoutEffect(() => {
    const root = document.documentElement;
    const selector = '.chat-window'; // wichtig: passt zu deinem DOM
    const measure = () => {
      const el = document.querySelector(selector);
      if (!el) return;
      const r = el.getBoundingClientRect();
      // linke und rechte "Freiräume" relativ zum Viewport
      root.style.setProperty('--chat-left', `${Math.round(r.left)}px`);
      root.style.setProperty('--chat-right', `${Math.round(window.innerWidth - r.right)}px`);
    };
    measure();

    // Reagiert auf Resize & Layout-Änderungen
    const ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(measure) : null;
    ro?.observe(document.body);

    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    // kleine Verzögerung nach Content-Loads (Fonts, Sidebar-Toggle etc.)
    const t = setTimeout(measure, 50);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      clearTimeout(t);
    };
  }, [isSidebarVisible, currentSessionId]);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        // Lade nur die Top 20 Sessions (server-seitig sortiert nach updatedAt)
        // Der Server sollte standardmäßig nach updatedAt sortieren
        const response = await api.get(`/database/sessions?limit=20&sort=-updatedAt`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        let sessionsList = response.data;

        // Wenn die aktuelle Session nicht in den Top 20 ist, lade sie separat und füge sie hinzu
        if (currentSessionId && !sessionsList.find(s => s._id === currentSessionId)) {
          try {
            const currentSessionResponse = await api.get(`/database/sessions/${currentSessionId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            // Füge die aktuelle Session hinzu und entferne die letzte
            sessionsList = [currentSessionResponse.data, ...sessionsList.slice(0, 19)];
          } catch (err) {
            // Session existiert nicht mehr (gelöscht), ignorieren
            console.log('Current session not found:', currentSessionId);
          }
        }

        setSessions(sessionsList);
      } catch (e) {
        // Ignoriere abgebrochene Requests (normal beim Session-Wechsel)
        if (e.name === 'CanceledError' || e.name === 'AbortError') {
          return;
        }
        console.error('Error fetching sessions:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
    // Kein automatisches Polling mehr - Updates werden durch refreshCurrentSession getriggert
    // const interval = setInterval(() => fetchSessions(), 5000);
    // return () => clearInterval(interval);
  }, [token, currentSessionId]);

  // Prüfe ob die aktuelle Session noch in der Liste existiert
  useEffect(() => {
    if (currentSessionId && sessions.length > 0) {
      const sessionExists = sessions.some(s => s._id === currentSessionId);
      if (!sessionExists) {
        // Session nicht mehr in der Liste (z.B. gelöscht)
        setCurrentSessionId(null);
      }
    }
  }, [sessions, currentSessionId]);

  const toggleSidebar = () => setIsSidebarVisible(v => !v);
  const showAppMenu   = () => {
    setCurrentSessionId(null);
    setShowTemplatesPage(false);
  };
  const showTemplates = () => {
    setCurrentSessionId(null);
    setShowTemplatesPage(true);
  };

  // Funktion um nur die aktuelle Session neu zu laden (z.B. nach Upload)
  const refreshCurrentSession = async () => {
    if (!currentSessionId) return;

    try {
      const response = await api.get(`/database/sessions/${currentSessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedSession = response.data;

      // Aktualisiere die Session im sessions Array
      setSessions(prevSessions =>
        prevSessions.map(s => s._id === currentSessionId ? updatedSession : s)
      );

      console.log('🔄 Current session refreshed, processing_document:', updatedSession.processing_document);
    } catch (err) {
      console.error('Error refreshing current session:', err);
    }
  };

  // Debounce für refreshSessions - verhindert zu häufige Updates
  const lastRefreshTime = React.useRef(0);
  const refreshDebounceTime = 2000; // 2 Sekunden zwischen Updates

  // Funktion um Sessions nach Nachricht neu zu laden
  const refreshSessions = async (sessionIdToRefresh = null) => {
    // Prüfe ob genug Zeit seit dem letzten Refresh vergangen ist
    const now = Date.now();
    if (now - lastRefreshTime.current < refreshDebounceTime) {
      console.log('⏱️ Refresh zu früh, überspringe...');
      return;
    }
    lastRefreshTime.current = now;

    try {
      // Lade nur die Top 20 Sessions (server-seitig sortiert)
      const response = await api.get(`/database/sessions?limit=20&sort=-updatedAt`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let sessionsList = response.data;

      // Wenn eine spezifische Session aktualisiert wurde und nicht in den Top 20 ist
      if (sessionIdToRefresh && !sessionsList.find(s => s._id === sessionIdToRefresh)) {
        try {
          const sessionResponse = await api.get(`/database/sessions/${sessionIdToRefresh}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          // Füge die Session hinzu und entferne die letzte
          sessionsList = [sessionResponse.data, ...sessionsList.slice(0, 19)];
        } catch (err) {
          console.log('Session not found:', sessionIdToRefresh);
        }
      }

      setSessions(sessionsList);
    } catch (e) {
      console.error('Error refreshing sessions:', e);
    }
  };

  const handleNewSessionApp = async (app_id) => {
    if (!app_id) {
      console.error('Error: app_id is undefined');
      return;
    }
    try {
      const response_app_name = await api.get(`/database/apps/${app_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Erstelle Session - Backend vergibt automatisch die session_number und updatedAt
      const response = await api.post(`/database/sessions`,
        { name: '...', display: '...', history: [], app: app_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Verwende die session_number vom Backend
      const sessionNumber = response.data.session_number;
      const finalName = `Session ${sessionNumber} - ${response_app_name.data.name}`;

      // Update den Session-Namen im Backend - Backend aktualisiert automatisch updatedAt
      await api.patch(`/database/sessions/${response.data._id}`,
        { name: finalName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Lade die aktualisierte Session vom Backend um die korrekte updatedAt zu bekommen
      const updatedSessionResponse = await api.get(`/database/sessions/${response.data._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const newSession = {
        ...updatedSessionResponse.data,
        deleted: false
      };

      setApp(app_id);
      setCurrentSessionId(newSession._id);

      // Aktualisiere die Session-Liste sofort mit der kompletten Session vom Server
      setSessions(prev => {
        // Entferne alte Version falls vorhanden
        const filtered = prev.filter(s => s._id !== newSession._id);
        // Füge neue Session hinzu und sortiere
        const updated = [newSession, ...filtered];
        return updated.sort((a,b) => new Date(b.updatedAt) - new Date(a.updatedAt)).slice(0, 20);
      });
    } catch (e) {
      console.error('Error creating new session:', e);
    }
  };

  const selectSession = async (index) => {
    if (index === null) {
      try {
        // Erstelle neue Session - Backend vergibt automatisch die session_number und updatedAt
        const response = await api.post(`/database/sessions`,
          { name: '...', display: '...', history: [] },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Verwende die session_number vom Backend
        const sessionNumber = response.data.session_number;
        const finalName = `Session ${sessionNumber}`;

        // Update den Session-Namen im Backend - Backend aktualisiert automatisch updatedAt
        await api.patch(`/database/sessions/${response.data._id}`,
          { name: finalName },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Lade die aktualisierte Session vom Backend um die korrekte updatedAt zu bekommen
        const updatedSessionResponse = await api.get(`/database/sessions/${response.data._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const createdSession = {
          ...updatedSessionResponse.data,
          deleted: false
        };

        setCurrentSessionId(response.data._id);

        // Aktualisiere die Session-Liste sofort mit der kompletten Session vom Server
        setSessions(prev => {
          // Entferne alte Version falls vorhanden
          const filtered = prev.filter(s => s._id !== createdSession._id);
          // Füge neue Session hinzu und sortiere
          const updated = [createdSession, ...filtered];
          return updated.sort((a,b) => new Date(b.updatedAt) - new Date(a.updatedAt)).slice(0, 20);
        });
      } catch (e) {
        console.error('Error creating new session:', e);
      }
    } else {
      // Session ID setzen basierend auf Index
      const selectedSession = sessions[index];
      if (selectedSession) {
        setShowTemplatesPage(false); // Templates-Seite schließen
        setCurrentSessionId(selectedSession._id);

        // Vollständige Session mit History nachladen, falls noch nicht vorhanden
        if (!selectedSession.history) {
          try {
            const response = await api.get(`/database/sessions/${selectedSession._id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            // Session in der Liste aktualisieren (basierend auf ID, nicht Index)
            setSessions(prev => prev.map((s) =>
              s._id === selectedSession._id ? { ...s, history: response.data.history || [] } : s
            ));
          } catch (e) {
            console.error('Error loading session details:', e);
          }
        }
      }
    }
  };

  if (loading) return <div className="loading">Lädt...</div>;

  return (
    <div className="app">
      <div className={`sidebar ${isSidebarVisible ? '' : 'collapsed'}`}>
        <Sidebar
          sessions={sessions}
          setSessions={setSessions}
          selectSession={selectSession}
          isVisible={isSidebarVisible}
          toggleSidebar={toggleSidebar}
          handleNewSessionApp={handleNewSessionApp}
          showAppMenu={showAppMenu}
          showTemplates={showTemplates}
          setToken={setToken}
          currentSession={currentSessionId ? sessions.find(s => s._id === currentSessionId) : null}
        />
      </div>

      <div className={`chat-container ${isSidebarVisible ? 'with-sidebar' : 'without-sidebar'}`}>
        {showTemplatesPage ? (
          <TemplatesPage onClose={showAppMenu} />
        ) : !currentSessionId ? (
          <AppMenu
            handleNewSessionApp={handleNewSessionApp}
            toggleSidebar={toggleSidebar}
            isVisible={isSidebarVisible}
          />
        ) : (
          <ChatWindow
            session={sessions.find(s => s._id === currentSessionId)}
            handleNewSessionApp={handleNewSessionApp}
            app={app}
            setApp={setApp}
            toggleSidebar={toggleSidebar}
            isVisible={isSidebarVisible}
            refreshSessions={refreshSessions}
            refreshCurrentSession={refreshCurrentSession}
          />
        )}
      </div>
    </div>
  );
};

export default ChatApp;
